import { useReducer, FC, useState, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import {
  AppContext,
  ApplicationState,
  getDefaultState,
} from "./models/applicationState";
import appReducer from "./reducers";
import { Context } from "./components/context";
import Telemetry from "./components/telemetry";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import Login from "./Login";
import React from "react";
import Layout from "./layout/mainLayout";
import settings from "./appSettings";
import AccessDenied from "./components/AccessDenied";
import { getInvokerUtility } from "./actions/userActions";
import { RedirectInProgressError, AuthLoopError } from "./services/MsGraphAuthService";
import { Box, CircularProgress, Typography } from "@mui/material";

type UserValidationStatus = "loading" | "valid" | "denied" | "error";

/**
 * Loading component shown while validating user authorization
 */
const AuthorizationLoader: FC = () => {
  const getLanguage = () =>
    window.localStorage.i18nextLng || navigator.language || "en";
  const isGerman = getLanguage().startsWith("de");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: 2
      }}>
      <CircularProgress size={48} />
      <Typography variant="body1" sx={{
        color: "text.secondary"
      }}>
        {isGerman ? "Autorisierung wird überprüft..." : "Verifying authorization..."}
      </Typography>
    </Box>
  );
};

export const App: FC = () => {
  const defaultState: ApplicationState = getDefaultState();
  const [applicationState, dispatch] = useReducer(appReducer, defaultState);
  const { accounts, inProgress } = useMsal();
  const [userStatus, setUserStatus] = useState<UserValidationStatus>("loading");

  const initialContext: AppContext = {
    state: applicationState,
    dispatch: dispatch,
  };

  // A calendar-connected personal account in MSAL cache should not count as
  // app-authenticated; only the app tenant account can access the app shell.
  const hasTenantAccount = accounts.some(
    (account) => account.tenantId === settings.tenantId,
  );

  // Validate user exists in backend when authenticated.
  // Once validated as "valid", we skip re-validation to prevent transient
  // MSAL token-refresh events from flashing the AccessDenied screen.
  useEffect(() => {
    if (!hasTenantAccount || inProgress !== "none") {
      // Wait for MsalProvider to finish any ongoing background interactions
      // or initializations before attempting to validate the user.
      if (userStatus !== "valid") {
        setUserStatus("loading");
      }
      return;
    }

    // Skip re-validation if user is already confirmed valid
    if (userStatus === "valid") {
      return;
    }

    let cancelled = false;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const validateUser = async (retryCount = 0) => {
      try {
        await getInvokerUtility();
        if (!cancelled) {
          setUserStatus("valid");
        }
      } catch (error: any) {
        if (cancelled) return;

        const status = error?.response?.status;
        if (status === 403 || status === 401) {
          setUserStatus("denied");
        } else if (error instanceof RedirectInProgressError) {
          // A login redirect is in progress (or was just triggered) —
          // the browser is navigating to the identity provider.
          // Stay in "loading" so the user sees a spinner, not AccessDenied.
          console.info(
            "[App] Auth redirect in progress, staying in loading state",
          );
        } else if (error instanceof AuthLoopError) {
          console.error("[App] Authentication loop or throttle detected");
          setUserStatus("error");
        } else if (retryCount < 2) {
          // Retry for transient errors (network, token acquisition)
          console.warn(
            `[App] User validation attempt ${retryCount + 1} failed, retrying...`,
            error,
          );
          retryTimeoutId = setTimeout(() => {
            if (!cancelled) validateUser(retryCount + 1);
          }, 1000 * (retryCount + 1));
        } else {
          // Exhausted retries — show error state
          console.error("[App] User validation failed:", error);
          setUserStatus("error");
        }
      }
    };

    validateUser();

    return () => {
      cancelled = true;
      if (retryTimeoutId !== null) {
        clearTimeout(retryTimeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userStatus intentionally omitted; we read it but must not react to it
  }, [hasTenantAccount, inProgress]);

  return (
    <div>

      <AuthenticatedTemplate>
        {hasTenantAccount ? (
          <>
            {userStatus === "loading" && <AuthorizationLoader />}
            {userStatus === "denied" && <AccessDenied />}
            {userStatus === "error" && (
              <AccessDenied
                message="An error occurred while verifying your access. Please try again later."
              />
            )}
            {userStatus === "valid" && (
              <Context.Provider value={initialContext}>
                <BrowserRouter>
                  <Telemetry>
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <Layout />
                    </React.Suspense>
                  </Telemetry>
                </BrowserRouter>
              </Context.Provider>
            )}
          </>
        ) : (
          <Login />
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
    </div>
  );
};
